import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, MessageCircle, RefreshCw, ShieldAlert, Sparkles } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  type TooltipProps,
} from "recharts";
import { MasterShell } from "@/components/master-shell";
import { MasterBillingTab } from "@/components/master/master-billing-tab";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  useUazapiSettings,
  useUpdateUazapiSettings,
  useWhatsappIntegrationsDashboard,
  useFaceVerificationSettings,
  useUpdateFaceVerificationSettings,
  useSecurityWhatsapp,
  useConnectSecurityWhatsapp,
  useDisconnectSecurityWhatsapp,
  useSecurityWhatsappGroups,
  useSetSecurityWhatsappGroup,
  useTestSecurityWhatsapp,
  useCompanyWhatsappList,
  useAttachCompanyExternalInstance,
  useDetachCompanyExternalInstance,
} from "@/hooks/queries/platform";
import { PlatformApiError } from "@/lib/api/gpsdrive-platform-client";
import {
  gpsDrivePlatformIntegrationsService,
  type FaceVerificationProvider,
  type CompanyWhatsappRow,
  WhatsappIntegrationsDashboard,
} from "@/services/gpsdrive-platform-integrations.service";

export const Route = createFileRoute("/master/integrations")({
  component: () => (
    <MasterShell>
      <IntegrationsPage />
    </MasterShell>
  ),
  head: () => ({ meta: [{ title: "Integrações — Super Master — GPS Drive" }] }),
});

const DAY_OPTIONS = [7, 30, 90] as const;

function IntegrationsPage() {
  const [days, setDays] = useState<(typeof DAY_OPTIONS)[number]>(30);

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Integrações</h1>
        <p className="text-sm text-muted-foreground">
          Credenciais centralizadas de plataforma — nunca configuradas por empresa.
        </p>
      </div>

      <Tabs defaultValue="whatsapp">
        <TabsList>
          <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
          <TabsTrigger value="openai">OpenAI</TabsTrigger>
          <TabsTrigger value="billing">Cobrança & Consultas</TabsTrigger>
        </TabsList>

        <TabsContent value="whatsapp" className="space-y-6 pt-4">
          <p className="text-sm text-muted-foreground">
            Conta admin centralizada da uazapi (WhatsApp) e acompanhamento real de como a
            integração está se comportando em todas as empresas.
          </p>

          <UazapiSettingsCard />

          <SecurityWhatsappCard />

          <CompanyWhatsappCard />

          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              WhatsApp — comportamento
            </h2>
            <div className="flex gap-1">
              {DAY_OPTIONS.map((d) => (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  className={cn(
                    "rounded-md border px-3 py-1 text-xs font-semibold transition-colors",
                    days === d
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:bg-accent",
                  )}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>

          <WhatsappDashboard days={days} />
        </TabsContent>

        <TabsContent value="openai" className="pt-4">
          <FaceVerificationSettingsCard />
        </TabsContent>

        <TabsContent value="billing" className="pt-4">
          <MasterBillingTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const FACE_VERIFICATION_PROVIDER_LABEL: Record<FaceVerificationProvider, string> = {
  openai: "OpenAI (ChatGPT)",
  gemini: "Google Gemini",
};

// Verificação de rosto na assinatura de contrato por autoatendimento
// (pedido 2026-09-11, revisado no mesmo dia: NUNCA por empresa —
// configuração única pra toda a plataforma, mesmo espírito do card uazapi
// acima). Usada por contrato.assinar.$token.tsx (passo "Tire uma foto").
function FaceVerificationSettingsCard() {
  const { data: settings, isLoading } = useFaceVerificationSettings();
  const update = useUpdateFaceVerificationSettings();

  const [provider, setProvider] = useState<FaceVerificationProvider>("openai");
  const [apiKey, setApiKey] = useState("");
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setProvider(settings.provider ?? "openai");
    setEnabled(settings.enabled);
  }, [settings]);

  const handleSave = async () => {
    if (enabled && !apiKey.trim() && !settings?.hasApiKey) {
      toast.error("Informe a chave de API antes de ativar");
      return;
    }
    try {
      await update.mutateAsync({
        provider,
        apiKey: apiKey.trim() || undefined,
        enabled,
      });
      toast.success("Verificação de rosto salva");
      setApiKey("");
    } catch (err) {
      toast.error(err instanceof PlatformApiError ? err.message : "Falha ao salvar a configuração");
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-emerald-600">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">Verificação de rosto (assinatura de contrato)</CardTitle>
              {settings?.hasApiKey && settings.enabled && (
                <Badge className="border-transparent bg-emerald-500 text-white hover:bg-emerald-500">
                  Ativo
                </Badge>
              )}
            </div>
            <CardDescription>
              Ao tirar a foto na assinatura por autoatendimento (contrato.assinar), o sistema
              confirma com o provedor escolhido que o rosto da pessoa foi capturado corretamente.
              Sem chave configurada, a foto é aceita sem checagem — nunca trava a assinatura.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Provedor</Label>
          <Select value={provider} onValueChange={(v) => setProvider(v as FaceVerificationProvider)}>
            <SelectTrigger disabled={isLoading}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.entries(FACE_VERIFICATION_PROVIDER_LABEL) as [FaceVerificationProvider, string][]).map(
                ([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="face-verification-api-key">Chave de API</Label>
          <Input
            id="face-verification-api-key"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={settings?.hasApiKey ? "•••••••••••••••• (chave já configurada)" : "Cole a chave de API"}
            disabled={isLoading}
          />
          <p className="text-xs text-muted-foreground">
            {settings?.hasApiKey
              ? "Chave configurada. Deixe em branco para manter a já salva."
              : "Nenhuma chave configurada — a verificação fica desligada até salvar uma."}
          </p>
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4"
          />
          Ativar verificação de rosto
        </label>
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={update.isPending || isLoading}>
            {update.isPending ? "Salvando…" : "Salvar"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Diferente do card da tela Administração > Integrações (por empresa, só
// "Conectar"/QR code): aqui é a credencial ADMIN da conta uazapi inteira
// (cria/gerencia a instância de cada empresa), pedido 2026-09-08 pra sair
// de "editar a variável de ambiente do servidor" para "configurar pelo
// painel". Banco tem prioridade sobre env.ts assim que salvo aqui (ver
// UazapiClient no backend).
function UazapiSettingsCard() {
  const { data: settings, isLoading } = useUazapiSettings();
  const update = useUpdateUazapiSettings();
  const [baseUrl, setBaseUrl] = useState("");
  const [adminToken, setAdminToken] = useState("");

  useEffect(() => {
    if (settings) setBaseUrl(settings.baseUrl);
  }, [settings]);

  const handleSave = async () => {
    try {
      await update.mutateAsync({
        baseUrl: baseUrl.trim() || undefined,
        adminToken: adminToken.trim() || undefined,
      });
      toast.success("Configuração da uazapi salva");
      setAdminToken("");
    } catch (err) {
      toast.error(err instanceof PlatformApiError ? err.message : "Falha ao salvar a configuração");
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md"
            style={{ backgroundColor: "#25D366" }}
          >
            <MessageCircle className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">uazapi — conta admin (WhatsApp)</CardTitle>
              {settings?.hasAdminToken && (
                <Badge className="border-transparent bg-emerald-500 text-white hover:bg-emerald-500">
                  Configurado
                </Badge>
              )}
            </div>
            <CardDescription>
              Credencial centralizada usada para criar/gerenciar a instância uazapi de cada empresa
              (Administração &gt; Integrações, card WhatsApp).
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="uazapi-base-url">URL base da uazapi</Label>
          <Input
            id="uazapi-base-url"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://api.uazapi.com"
            disabled={isLoading}
          />
          {settings && (
            <p className="text-xs text-muted-foreground">
              {settings.baseUrlFromDatabase
                ? "Configurada aqui no painel."
                : "Usando o valor padrão/variável de ambiente do servidor — salve aqui para passar a controlar pelo painel."}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="uazapi-admin-token">Token admin</Label>
          <Input
            id="uazapi-admin-token"
            type="password"
            value={adminToken}
            onChange={(e) => setAdminToken(e.target.value)}
            placeholder={settings?.hasAdminToken ? "•••••••••••••••• (token configurado)" : "Cole o admintoken da uazapi"}
            disabled={isLoading}
          />
          <p className="text-xs text-muted-foreground">
            {settings?.adminTokenFromDatabase
              ? "Configurado aqui no painel. Deixe em branco para manter o token já salvo."
              : settings?.hasAdminToken
                ? "Usando a variável de ambiente do servidor (UAZAPI_ADMIN_TOKEN). Preencha para passar a controlar pelo painel."
                : "Nenhum token configurado nesta implantação — o WhatsApp fica indisponível para todas as empresas até configurar."}
          </p>
        </div>
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={update.isPending || isLoading}>
            {update.isPending ? "Salvando…" : "Salvar"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Alertas de segurança do servidor (pedido 2026-09-24): número próprio da
// PLATAFORMA (não o de nenhuma empresa) conectado por QR aqui, e um grupo
// do WhatsApp escolhido para receber os avisos de login/tentativas SSH
// (monitor /opt/gpsdrive/ssh-alert → POST /internal/security-alert).
function SecurityWhatsappCard() {
  const qc = useQueryClient();
  const [qrcode, setQrcode] = useState<string | null>(null);
  const { data: status, isLoading } = useSecurityWhatsapp({ pollWhileConnecting: qrcode !== null });
  const connect = useConnectSecurityWhatsapp();
  const disconnect = useDisconnectSecurityWhatsapp();
  const connected = Boolean(status?.connected);
  const groups = useSecurityWhatsappGroups(connected);
  const setGroup = useSetSecurityWhatsappGroup();
  const test = useTestSecurityWhatsapp();
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (connected) setQrcode(null);
  }, [connected]);

  const errorMessage = (err: unknown, fallback: string) =>
    err instanceof PlatformApiError ? err.message : fallback;

  const handleConnect = async () => {
    try {
      const result = await connect.mutateAsync();
      if (result.connected) toast.success("WhatsApp conectado");
      else if (result.qrcode) setQrcode(result.qrcode);
      else toast.error("A uazapi não retornou um QR code — tente novamente");
    } catch (err) {
      toast.error(errorMessage(err, "Falha ao iniciar conexão com o WhatsApp"));
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect.mutateAsync();
    } catch (err) {
      toast.error(errorMessage(err, "Falha ao desconectar o WhatsApp"));
    }
  };

  const handleRefreshGroups = async () => {
    setRefreshing(true);
    try {
      const data = await gpsDrivePlatformIntegrationsService.listSecurityWhatsappGroups(true);
      qc.setQueryData(["platform", "integrations", "security-whatsapp", "groups"], data);
    } catch (err) {
      toast.error(errorMessage(err, "Falha ao atualizar a lista de grupos"));
    } finally {
      setRefreshing(false);
    }
  };

  const handleSelectGroup = async (jid: string) => {
    const group = groups.data?.groups.find((g) => g.jid === jid);
    if (!group) return;
    try {
      await setGroup.mutateAsync(group);
    } catch (err) {
      toast.error(errorMessage(err, "Falha ao salvar o grupo"));
    }
  };

  const handleTest = async () => {
    try {
      await test.mutateAsync();
      toast.success("Mensagem de teste enviada ao grupo");
    } catch (err) {
      toast.error(errorMessage(err, "Falha ao enviar o teste"));
    }
  };

  const groupList = groups.data?.groups ?? [];
  const savedGroupMissing =
    status?.groupJid && groups.data && !groupList.some((g) => g.jid === status.groupJid);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-red-600">
            <ShieldAlert className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">Alertas de segurança do servidor</CardTitle>
              {connected && status?.groupJid && (
                <Badge className="border-transparent bg-emerald-500 text-white hover:bg-emerald-500">
                  Ativo
                </Badge>
              )}
            </div>
            <CardDescription>
              Número de WhatsApp da plataforma (separado das empresas) que avisa num grupo sobre
              logins e tentativas de acesso ao servidor.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {status && !status.available ? (
          <p className="text-sm text-muted-foreground">
            Configure o token admin da uazapi acima antes de conectar.
          </p>
        ) : isLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : connected ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm">
                Conectado{status?.phone ? " como " : ""}
                {status?.phone && <span className="font-medium">{status.phone}</span>}.
              </p>
              <Button variant="outline" size="sm" onClick={handleDisconnect} disabled={disconnect.isPending}>
                {disconnect.isPending ? "Desconectando…" : "Desconectar"}
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Grupo que recebe os alertas</Label>
              <div className="flex gap-2">
                <Select
                  value={status?.groupJid && !savedGroupMissing ? status.groupJid : undefined}
                  onValueChange={handleSelectGroup}
                  disabled={groups.isLoading || setGroup.isPending}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue
                      placeholder={
                        groups.isLoading
                          ? "Carregando grupos…"
                          : groupList.length === 0
                            ? "Nenhum grupo encontrado neste número"
                            : "Escolha um grupo"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {groupList.map((g) => (
                      <SelectItem key={g.jid} value={g.jid}>
                        {g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleRefreshGroups}
                  disabled={refreshing}
                  title="Atualizar lista de grupos"
                >
                  <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
                </Button>
              </div>
              {groups.isError && (
                <p className="text-xs text-red-600">Não foi possível carregar os grupos deste número.</p>
              )}
              {savedGroupMissing && (
                <p className="text-xs text-amber-600">
                  O grupo salvo ({status?.groupName}) não aparece mais neste número — escolha outro.
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                O número conectado precisa participar do grupo. Criou o grupo agora? Clique em
                atualizar.
              </p>
            </div>

            <div className="flex justify-end">
              <Button onClick={handleTest} disabled={!status?.groupJid || test.isPending}>
                {test.isPending ? "Enviando…" : "Enviar teste"}
              </Button>
            </div>
          </>
        ) : qrcode ? (
          <div className="flex flex-col items-center gap-3">
            <img
              src={qrcode.startsWith("data:") ? qrcode : `data:image/png;base64,${qrcode}`}
              alt="QR code do WhatsApp"
              className="h-56 w-56 rounded-md border border-border"
            />
            <p className="text-center text-xs text-muted-foreground">
              No celular do número da plataforma: WhatsApp → Aparelhos conectados → Conectar um
              aparelho → escaneie este código.
            </p>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">Nenhum número conectado.</p>
            <Button onClick={handleConnect} disabled={connect.isPending}>
              {connect.isPending ? "Gerando QR code…" : "Conectar WhatsApp"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// "WhatsApp das empresas" (pedido 2026-09-25): vincula a uma empresa uma
// instância uazapi que JÁ existe (mesmo servidor, usada também por outro
// sistema) colando o token dela — sem QR code, a empresa não precisa
// fazer nada. O backend nunca mexe no webhook nem desloga essa instância;
// "Desvincular" só esquece o token aqui.
function CompanyWhatsappCard() {
  const { data: rows, isLoading, isError } = useCompanyWhatsappList();
  const attach = useAttachCompanyExternalInstance();
  const detach = useDetachCompanyExternalInstance();
  const [target, setTarget] = useState<CompanyWhatsappRow | null>(null);
  const [token, setToken] = useState("");
  const [error, setError] = useState<string | null>(null);

  const openAttach = (row: CompanyWhatsappRow) => {
    setTarget(row);
    setToken("");
    setError(null);
  };

  const handleAttach = async () => {
    if (!target) return;
    setError(null);
    try {
      await attach.mutateAsync({ companyId: target.companyId, token: token.trim() });
      setTarget(null);
    } catch (err) {
      setError(err instanceof PlatformApiError ? err.message : "Falha ao vincular a instância");
    }
  };

  const handleDetach = async (row: CompanyWhatsappRow) => {
    try {
      await detach.mutateAsync(row.companyId);
    } catch (err) {
      toast.error(err instanceof PlatformApiError ? err.message : "Falha ao desvincular");
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-emerald-600">
            <MessageCircle className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <CardTitle className="text-base">WhatsApp das empresas</CardTitle>
            <CardDescription>
              Vincule a uma empresa uma instância da uazapi que já está conectada (inclusive usada
              em outro sistema) — sem QR code e sem reconectar.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <p className="px-6 pb-6 text-sm text-red-600">Não foi possível carregar as empresas.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Número</TableHead>
                  <TableHead className="text-right">Ação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(rows ?? []).map((row) => {
                  const connected = row.hasInstance && row.status === "connected";
                  return (
                    <TableRow key={row.companyId}>
                      <TableCell className="font-medium">{row.companyName}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1">
                          <Badge
                            variant="outline"
                            className={cn(
                              connected
                                ? "border-emerald-500 text-emerald-600"
                                : row.hasInstance
                                  ? "border-amber-500 text-amber-600"
                                  : "text-muted-foreground",
                            )}
                          >
                            {connected ? "Conectado" : row.hasInstance ? "Desconectado" : "Sem WhatsApp"}
                          </Badge>
                          {row.external && <Badge variant="secondary">Instância existente</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{row.phone ?? "—"}</TableCell>
                      <TableCell className="text-right">
                        {row.external ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDetach(row)}
                            disabled={detach.isPending && detach.variables === row.companyId}
                          >
                            Desvincular
                          </Button>
                        ) : (
                          <Button variant="outline" size="sm" onClick={() => openAttach(row)}>
                            Usar instância existente
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Dialog open={target !== null} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Usar instância existente</DialogTitle>
            <DialogDescription>
              {target?.companyName}: cole o token da instância (painel da uazapi). O WhatsApp continua
              funcionando normalmente no outro sistema — não mexemos no webhook nem desconectamos.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="external-instance-token">Token da instância</Label>
            <Input
              id="external-instance-token"
              type="password"
              autoComplete="off"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Token da instância na uazapi"
            />
            {target?.hasInstance && (
              <p className="text-xs text-amber-600">
                Esta empresa já tem um WhatsApp conectado por QR code aqui — ele será substituído por
                esta instância.
              </p>
            )}
            {error && <p className="text-xs text-red-600">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)}>
              Cancelar
            </Button>
            <Button onClick={handleAttach} disabled={token.trim().length < 8 || attach.isPending}>
              {attach.isPending ? "Verificando…" : "Vincular"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

const CONNECTION_COLORS = {
  connected: "#10b981",
  connecting: "#f59e0b",
  disconnected: "#94a3b8",
} as const;

const SEND_COLORS = {
  success: "#10b981",
  failed: "#f43f5e",
} as const;

function StatTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone: "good" | "warning" | "critical" | "neutral";
}) {
  const toneClasses = {
    good: "border-emerald-500/30 bg-emerald-500/5 text-emerald-600 dark:text-emerald-400",
    warning: "border-amber-500/30 bg-amber-500/5 text-amber-600 dark:text-amber-400",
    critical: "border-red-500/30 bg-red-500/5 text-red-600 dark:text-red-400",
    neutral: "border-border bg-muted/30 text-foreground",
  }[tone];
  return (
    <div className={cn("rounded-xl border p-4", toneClasses)}>
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-bold">{value}</div>
    </div>
  );
}

function SendsTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  const success = payload.find((p) => p.dataKey === "success")?.value ?? 0;
  const failed = payload.find((p) => p.dataKey === "failed")?.value ?? 0;
  const dateLabel = typeof label === "string" ? format(new Date(`${label}T00:00:00`), "dd/MM/yyyy", { locale: ptBR }) : "";
  return (
    <div className="rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-semibold text-popover-foreground">{dateLabel}</div>
      <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Sucesso: {success}
      </div>
      <div className="flex items-center gap-1.5 text-rose-600 dark:text-rose-400">
        <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
        Falha: {failed}
      </div>
    </div>
  );
}

const KIND_LABELS: Record<string, string> = {
  welcome: "Boas-vindas",
  communication_failure: "Falha de comunicação",
  contract_signing_link: "Link de assinatura de contrato",
  login: "Envio de login",
  security_alert: "Alerta de segurança",
  power_cut: "Corte de energia",
};

function WhatsappDashboard({ days }: { days: number }) {
  const { data, isLoading, isError } = useWhatsappIntegrationsDashboard(days);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-20 w-full animate-pulse rounded-xl bg-muted" />
          ))}
        </div>
        <div className="h-64 w-full animate-pulse rounded-xl bg-muted" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        Não foi possível carregar o dashboard agora.
      </div>
    );
  }

  return <WhatsappDashboardContent data={data} days={days} />;
}

function WhatsappDashboardContent({ data, days }: { data: WhatsappIntegrationsDashboard; days: number }) {
  const { connection, sends } = data;
  const totalSends = sends.totals.success + sends.totals.failed;
  const successRate = totalSends > 0 ? Math.round((sends.totals.success / totalSends) * 100) : null;

  const connectionData = [
    { key: "connected", label: "Conectado", value: connection.connected, color: CONNECTION_COLORS.connected },
    { key: "connecting", label: "Conectando", value: connection.connecting, color: CONNECTION_COLORS.connecting },
    { key: "disconnected", label: "Desconectado", value: connection.disconnected, color: CONNECTION_COLORS.disconnected },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatTile label={`Enviados com sucesso (${days}d)`} value={sends.totals.success} tone="good" />
        <StatTile
          label={`Falharam (${days}d)`}
          value={sends.totals.failed}
          tone={sends.totals.failed > 0 ? "critical" : "neutral"}
        />
        <StatTile
          label="Taxa de sucesso"
          value={successRate !== null ? `${successRate}%` : "—"}
          tone={successRate === null ? "neutral" : successRate >= 95 ? "good" : successRate >= 80 ? "warning" : "critical"}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Conexão por empresa</CardTitle>
            <CardDescription>Empresas que já clicaram "Conectar" pelo menos uma vez.</CardDescription>
          </CardHeader>
          <CardContent>
            {connection.total === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhuma empresa conectou o WhatsApp ainda.</p>
            ) : (
              <div className="flex items-center gap-6">
                <div className="relative h-28 w-28 shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={connectionData}
                        dataKey="value"
                        nameKey="label"
                        innerRadius={38}
                        outerRadius={54}
                        paddingAngle={connectionData.filter((d) => d.value > 0).length > 1 ? 3 : 0}
                        cornerRadius={4}
                        stroke="none"
                      >
                        {connectionData.map((d) => (
                          <Cell key={d.key} fill={d.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-lg font-bold leading-none">{connection.total}</span>
                    <span className="text-[10px] text-muted-foreground">empresas</span>
                  </div>
                </div>
                <div className="flex-1 space-y-1.5">
                  {connectionData.map((d) => (
                    <div key={d.key} className="flex items-center justify-between gap-2 text-xs">
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <span className="h-2 w-2 rounded-full" style={{ background: d.color }} />
                        {d.label}
                      </span>
                      <span className="font-semibold text-foreground">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Volume de envio por dia</CardTitle>
            <CardDescription>Boas-vindas + avisos de falha de comunicação, sucesso vs. erro.</CardDescription>
          </CardHeader>
          <CardContent>
            {totalSends === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhum envio registrado no período.</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={sends.daily}>
                  <CartesianGrid vertical={false} stroke="var(--border)" strokeOpacity={0.6} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d: string) => format(new Date(`${d}T00:00:00`), "dd/MM")}
                    tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                    axisLine={false}
                    tickLine={false}
                    interval={Math.max(0, Math.ceil(sends.daily.length / 8) - 1)}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                    axisLine={false}
                    tickLine={false}
                    width={28}
                  />
                  <Tooltip content={<SendsTooltip />} cursor={{ fill: "var(--muted)", opacity: 0.4 }} />
                  <Bar dataKey="success" name="Sucesso" stackId="a" fill={SEND_COLORS.success} maxBarSize={18} />
                  <Bar
                    dataKey="failed"
                    name="Falha"
                    stackId="a"
                    fill={SEND_COLORS.failed}
                    radius={[3, 3, 0, 0]}
                    maxBarSize={18}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
            <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full" style={{ background: SEND_COLORS.success }} />
                Sucesso
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full" style={{ background: SEND_COLORS.failed }} />
                Falha
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {sends.recentFailures.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Falhas recentes</CardTitle>
            <CardDescription>Últimos envios que falharam, de qualquer período (até 20).</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Erro</TableHead>
                  <TableHead className="text-right">Quando</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sends.recentFailures.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell className="font-medium">{f.companyName}</TableCell>
                    <TableCell>{KIND_LABELS[f.kind] ?? f.kind}</TableCell>
                    <TableCell
                      className="max-w-xs truncate text-xs text-muted-foreground"
                      title={f.errorMessage ?? undefined}
                    >
                      {f.errorMessage ?? "—"}
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">
                      {format(new Date(f.createdAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
